import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { GraduationCap, Plus, X } from 'lucide-react';

const C3_TheoreticalFoundations = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  const [theories, setTheories] = useState(
    dataC?.theoreticalFoundations || [
      { name: '', author: '', connection: 'built on' },
      { name: '', author: '', connection: 'built on' },
      { name: '', author: '', connection: 'built on' }
    ]
  );

  useEffect(() => {
    if (dataC?.theoreticalFoundations && dataC.theoreticalFoundations.length > 0) {
      setTheories(dataC.theoreticalFoundations);
    }
  }, [dataC?.theoreticalFoundations]);

  const connectionTypes = [
    { value: 'built on', label: 'Built on', description: 'Your framework is directly grounded in this theory' },
    { value: 'extends', label: 'Extends', description: 'Your framework expands or builds upon this theory' },
    { value: 'integrates', label: 'Integrates', description: 'Your framework combines elements from this theory' },
    { value: 'adapts', label: 'Adapts', description: 'Your framework modifies this theory for a new context' },
    { value: 'challenges', label: 'Challenges', description: 'Your framework critiques or offers alternative to this theory' }
  ];

  const updateTheory = (index, field, value) => {
    const updated = [...theories];
    updated[index] = { ...updated[index], [field]: value };
    setTheories(updated);
    updateDataRaw({ 'wizardC.theoreticalFoundations': updated });
  };

  const addTheory = () => {
    if (theories.length < 6) {
      const updated = [...theories, { name: '', author: '', connection: 'built on' }];
      setTheories(updated);
      updateDataRaw({ 'wizardC.theoreticalFoundations': updated });
    }
  };

  const removeTheory = (index) => {
    if (theories.length > 1) {
      const updated = theories.filter((_, i) => i !== index);
      setTheories(updated);
      updateDataRaw({ 'wizardC.theoreticalFoundations': updated });
    }
  };

  const filledTheories = theories.filter(t => t.name.trim() !== '');

  const handleContinue = () => {
    updateDataRaw({ 'wizardC.theoreticalFoundations': theories });
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon theoretical-icon">
          <GraduationCap size={32} />
        </div>
        <div>
          <h2>Step 3: Theoretical Foundations</h2>
          <p className="step-description">Document the theories that inform your framework</p>
        </div>
      </div>

      <div className="step-content">
        <div className="concept-definition">
          <h3>What Are Theoretical Foundations?</h3>
          <p>
            Your theoretical foundation describes the <strong>underlying theories or prior frameworks</strong> that 
            inform your research. It shows the intellectual lineage of your work and justifies your framework's structure.
          </p>
        </div>

        <div className="theory-examples">
          <h4>How Theories Connect to Your Framework:</h4>
          <div className="connection-types-grid">
            {connectionTypes.map(type => (
              <div key={type.value} className="connection-type-item">
                <span className="connection-label">{type.label}:</span>
                <span className="connection-desc">{type.description}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="theory-input-section">
          <div className="theory-section-header">
            <h4>Document Your Theories</h4>
            <span className="theory-counter">{filledTheories.length} of {theories.length} documented</span>
          </div>
          
          <p className="input-instruction">
            Enter the theories that inform your framework. You can add up to 6 theories.
          </p>

          <div className="theory-inputs-list">
            {theories.map((theory, index) => (
              <div key={index} className="theory-input-card">
                <div className="theory-card-header">
                  <span className="theory-number">Theory {index + 1}</span>
                  {theories.length > 1 && (
                    <button 
                      className="remove-theory-btn"
                      onClick={() => removeTheory(index)}
                      title="Remove theory"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <div className="theory-input-fields">
                  <div className="theory-field">
                    <label>Theory Name</label>
                    <input
                      type="text"
                      className="theory-input"
                      placeholder="e.g., Social Cognitive Theory"
                      value={theory.name}
                      onChange={(e) => updateTheory(index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="theory-field">
                    <label>Author & Year</label>
                    <input
                      type="text"
                      className="theory-input"
                      placeholder="e.g., Bandura, 1986"
                      value={theory.author}
                      onChange={(e) => updateTheory(index, 'author', e.target.value)}
                    />
                  </div>
                  <div className="theory-field">
                    <label>Connection Type</label>
                    <select
                      className="theory-select"
                      value={theory.connection}
                      onChange={(e) => updateTheory(index, 'connection', e.target.value)}
                    >
                      {connectionTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {theories.length < 6 && (
            <button className="add-theory-btn" onClick={addTheory}>
              <Plus size={16} />
              Add Another Theory
            </button>
          )}
        </div>

        <div className="theory-importance">
          <h4>Why Theoretical Foundations Matter:</h4>
          <div className="importance-grid">
            <div className="importance-item">
              <span className="importance-icon">✓</span>
              <span><strong>Establishes credibility</strong> — shows your work is grounded in established knowledge</span>
            </div>
            <div className="importance-item">
              <span className="importance-icon">✓</span>
              <span><strong>Shows intellectual lineage</strong> — connects your work to the scholarly conversation</span>
            </div>
            <div className="importance-item">
              <span className="importance-icon">✓</span>
              <span><strong>Justifies your choices</strong> — explains why you've structured your framework this way</span>
            </div>
            <div className="importance-item">
              <span className="importance-icon">✓</span>
              <span><strong>Identifies gaps you're filling</strong> — shows your unique contribution</span>
            </div>
          </div>
        </div>

        <div className="tip-box">
          <strong>Tip:</strong> Even if you're developing a new framework, it should be informed by existing 
          theories. Reference at least 1-2 key theories that provide the foundation for your thinking.
        </div>
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={true}
        continueLabel="Continue to Theory Linkage"
      />
    </div>
  );
};

export default C3_TheoreticalFoundations;
